import { useCallback } from "react"
import { Creators as DataCreators } from "model/data"
import { Creators as NotificationCreators } from "modules/notifications/model"
import { useAppDispatch, useAppStore } from "hooks/redux"
import {
  useRenameInstance,
  DASHBOARD_INSTANCES_RESOURCE,
} from "resources/dashboard-instances"
import { instancesSlice } from "../store/instances-slice"

export interface RenameDashboard {
  // Renames the instance, not the backing layout. Never throws — toasts on success/failure.
  rename: (instanceId: string, name: string) => Promise<void>
}

/**
 * Renames an instance: PATCH, upsert the registry slice (immediate UI), and reset the instances
 * cache so the next list read refetches the renamed row.
 */
export function useRenameDashboard(): RenameDashboard {
  const store = useAppStore()
  const dispatch = useAppDispatch()
  const renameInstance = useRenameInstance()

  const rename = useCallback(
    async (instanceId: string, name: string) => {
      try {
        await renameInstance(instanceId, name)

        // Read fresh (not a render closure) so the upsert preserves metadata current at resolve time.
        const existing = store.getState().dashboard.instances[instanceId]
        if (existing) {
          dispatch(instancesSlice.actions.upsertInstance({ ...existing, name }))
        }

        // Reset so the next instances read returns the renamed row (the cached list predates it).
        dispatch(DataCreators.resetCollections([DASHBOARD_INSTANCES_RESOURCE]))
        dispatch(NotificationCreators.add("success", null, "Renamed the dashboard."))
      } catch {
        dispatch(NotificationCreators.add("error", null, "Couldn't rename the dashboard. Please try again."))
      }
    },
    [store, dispatch, renameInstance],
  )

  return { rename }
}
